import { useContext, useEffect, useRef, useState } from "react";
import { context } from "utils/Provider";
import {
  ClientsResponse,
  User,
  UserRole,
  useRolesQuery,
  useCreateClientMutation,
  useSignUpMutation,
  useUsersQuery,
  useCreateCubeMutation,
  useCubesQuery,
  useDeleteCubeMutation,
} from "graphql/types/graphql";
import { enqueueSnackbar } from "notistack";
import { DebouncedFunc, debounce } from "lodash";
import MDBox from "components/MDBox";
import UsersCard from "./components/UsersCard";
import FormsCard from "./components/FormsCard";

interface Cube {
  cubeId: string;
  name: string;
}

interface FormValue {
  cruxyId: string;
  role: string;
  email: string;
  password: string;
  repeatPassword: string;
}

interface CubeFormValue {
  name: string;
}

interface CompanyFormValue {
  companyId: string;
  companyName: string;
}

function ManageUsers(): JSX.Element {
  const { companies, token, setIsLoading, user, selectedCube, setSelectedCube } = useContext(context);

  const [errorEmail, setErrorEmail] = useState(false);
  const [errorRepeatPass, setErrorRepeatPass] = useState(false);
  const [errorPass, setErrorPass] = useState(false);
  const [errorUser, setErrorUser] = useState<string>(" ");
  const [errorCompany, setErrorCompany] = useState<string>(" ");
  const [errorCompanyId, setErrorCompanyId] = useState(false);
  const [errorCompanyName, setErrorCompanyName] = useState(false);
  const [errorCubeName, setErrorCubeName] = useState(false);
  const [users, setUsers] = useState<User[]>();
  const [roles, setRoles] = useState<string[]>();
  const [cubes, setCubes] = useState<Cube[]>();
  const [selectedCubeId, setSelectedCubeId] = useState<string | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [role, setRole] = useState<string | null>(null);
  const [cruxyId, setCruxyId] = useState<string | null>(null);

  const [signUp] = useSignUpMutation();
  const [createClient] = useCreateClientMutation();
  const [createCube] = useCreateCubeMutation();
  const [deleteCube] = useDeleteCubeMutation();

  const {
    data: dataUsers,
    loading: loadingUsers,
    error: errorUsers,
  } = useUsersQuery({
    variables: {
      filter: {
        cruxyId: cruxyId,
        role: UserRole[role as keyof typeof UserRole],
      },
    },
  });

  const { data: cubesData, loading: cubesLoading, error: cubesError } = useCubesQuery();
  const { data, loading, error } = useRolesQuery();
  const debounceRef = useRef<DebouncedFunc<(criteria: any) => void> | undefined>();

  useEffect(() => {
    if (loadingUsers || cubesLoading || !roles || !users) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [loadingUsers, cubesLoading, roles, users, setIsLoading]);

  useEffect(() => {
    if (data?.roles) {
      setRoles(data.roles);
    }
  }, [data, error]);

  useEffect(() => {
    if (dataUsers?.users) {
      setUsers(dataUsers?.users);
    }
  }, [dataUsers, errorUsers]);

  useEffect(() => {
    if (cubesData?.cubes) {
      setCubes(cubesData.cubes as Cube[]);
    }
  }, [cubesData, cubesError]);

  async function signUpHandler(values: FormValue) {
    try {
      setIsLoading(true);
      const response = await signUp({
        variables: {
          cruxyId: values.cruxyId,
          role: UserRole[values.role as keyof typeof UserRole],
          email: values.email,
          password: values.password,
        },
      });
      if (response) {
        enqueueSnackbar("User added successfully!", { variant: "success" });
        setIsLoading(false);
      }
    } catch (error: any) {
      setIsLoading(false);
      enqueueSnackbar(error.graphQLErrors[0].message ? error.graphQLErrors[0].message : "Something went wrong!", {
        variant: "error",
      });
    }
  }

  // async function newClientHandler(values: CompanyFormValue) {
  //   try {
  //     setIsLoading(true);
  //     const response = await createClient({
  //       variables: {
  //         cruxyId: values.companyId,
  //         name: values.companyName,
  //       },
  //     });
  //     console.log("response", response);
  //     if (response) {
  //       enqueueSnackbar("Company added successfully!", { variant: "success" });
  //       setIsLoading(false);
  //     }
  //   } catch (error: any) {
  //     console.log("err", JSON.stringify(error));
  //     setIsLoading(false);
  //     enqueueSnackbar(error.graphQLErrors[0].message ? error.graphQLErrors[0].message : "Something went wrong!", {
  //       variant: "error",
  //     });
  //   }
  // }

  async function newCubeHandler(values: CubeFormValue, { resetForm }: { resetForm: () => void }) {
    try {
      setIsLoading(true);
      const sanitizedName = values.name.replace(/[-|\s]+/g, "_");
      const response = await createCube({
        variables: {
          name: sanitizedName,
        },
        update: (cache) => {
          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "cubes",
          });
        },
      });
      if (response) {
        enqueueSnackbar("Cube added successfully!", { variant: "success" });
        resetForm();
        setIsLoading(false);
      }
    } catch (error: any) {
      setIsLoading(false);
      enqueueSnackbar(error.graphQLErrors[0]?.message || "Something went wrong!", { variant: "error" });
    }
  }

  async function handleDeleteCube() {
    if (!selectedCubeId) return;
    try {
      setDeleteLoading(true);
      const response = await deleteCube({
        variables: { cubeId: selectedCubeId },
        update: (cache) => {
          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "cubes",
          });
        },
      });
      if (response.data?.deleteCube?.success) {
        enqueueSnackbar("Cube deleted successfully!", { variant: "success" });
        setSelectedCubeId(null);
        setOpenDeleteDialog(false);
        if (selectedCube?.cubeId === selectedCubeId && cubes) {
          const remainingCubes = cubes.filter((cube) => cube.cubeId !== selectedCubeId);
          if (remainingCubes.length > 0) {
            setSelectedCube(remainingCubes[0]);
          } else {
            setSelectedCube(null);
          }
        }
      }
    } catch (error: any) {
      enqueueSnackbar(error.graphQLErrors[0].message ? error.graphQLErrors[0].message : "Something went wrong!", {
        variant: "error",
      });
    } finally {
      setDeleteLoading(false);
    }
  }

  return (
    <MDBox
      sx={{
        flex: 1,
        padding: "2rem",
        display: "flex",
        gap: 3,
      }}
    >
      {roles && users && (
        <>
          <UsersCard
            companies={companies}
            roles={roles}
            users={users}
            role={role}
            cruxyId={cruxyId}
            cubes={cubes}
            selectedCubeId={selectedCubeId}
            openDeleteDialog={openDeleteDialog}
            deleteLoading={deleteLoading}
            setCruxyId={setCruxyId}
            setRole={setRole}
            setOpenDeleteDialog={setOpenDeleteDialog}
            handleDeleteCube={handleDeleteCube}
          />
          <FormsCard
            user={user}
            companies={companies}
            roles={roles}
            cubes={cubes}
            selectedCubeId={selectedCubeId}
            errorEmail={errorEmail}
            errorRepeatPass={errorRepeatPass}
            errorPass={errorPass}
            errorUser={errorUser}
            errorCompany={errorCompany}
            errorCompanyId={errorCompanyId}
            errorCompanyName={errorCompanyName}
            errorCubeName={errorCubeName}
            cubesLoading={cubesLoading}
            deleteLoading={deleteLoading}
            setErrorEmail={setErrorEmail}
            setErrorRepeatPass={setErrorRepeatPass}
            setErrorPass={setErrorPass}
            setErrorUser={setErrorUser}
            setErrorCompany={setErrorCompany}
            setErrorCompanyId={setErrorCompanyId}
            setErrorCompanyName={setErrorCompanyName}
            setErrorCubeName={setErrorCubeName}
            setSelectedCubeId={setSelectedCubeId}
            setOpenDeleteDialog={setOpenDeleteDialog}
            signUpHandler={signUpHandler}
            newCubeHandler={newCubeHandler}
          />
        </>
      )}
    </MDBox>
  );
}

export default ManageUsers;
